import React from 'react'
import { Link, withRouter } from 'react-router-dom'
import SectionActions from './SectionActions'
import './FeatureTabs.less'

function FeatureTabs ({ match, basePath }) {
  const activeTab = match.params.tabId || 'weather-forecast'

  const tabs = [
    { id: 'weather-forecast', label: 'Weather Forecast', icon: 'forecast' },
    { id: 'dynamic-map', label: 'Dynamic Map', icon: 'map' },
    { id: 'dash', label: 'Dashboard', icon: 'dashboard' },
    { id: 'data-graphing', label: 'Data Graphing', icon: 'graph' },
    { id: 'alerts', label: 'Alerts', icon: 'alerts' }
  ]

  return (
    <>
      <div className='feature-tabs-wrapper'>
        <div className='feature-tabs'>
          {tabs.map(tab => (
            <Link
              key={tab.id}
              to={`${basePath}/${tab.id}`}
              className={`feature-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              <i className={`tab-icon ${tab.icon}`}></i>
              <span>{tab.label}</span>
            </Link>
          ))}
        </div>
      </div>

      <div className='feature-content'>
        {activeTab === 'weather-forecast' && (
          <div className='weather-forecast'>
            <div className='feature-intro'>
              <p>Plan smarter with hyperlocal forecasts powered by your weather station and nearby data sources, all in an ad-free experience.</p>
            </div>
            <div className='forecast-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Standard Forecast</h3>
                <div className='feature-image' />
                <div className='section-content'>
                  <div>
                    <h4>5-Day Hourly Forecast</h4>
                    <p>Get a clear five-day view with hourly updates for temperature and precipitation, keeping you informed of short-term weather changes.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>Advanced Forecast</h3>
                <div className='section-content'>
                  <div className='forecast-grid'>
                    <div>
                      <h3>AWN+ Forecast</h3>
                      <h4>10-Day Hourly Forecast</h4>
                      <p>Plan with confidence using a detailed 10-day, hour-by-hour forecast for temperature and precipitation to stay prepared for what's ahead.</p>
                    </div>
                    <div>
                      <h3>AWN+ for Teams Forecast</h3>
                      <h4>15-Day Hourly Forecast</h4>
                      <p>Plan even further ahead with a 15-day extended outlook, giving teams the insight they need to coordinate operations, outdoor activities, and long-term projects with confidence.</p>
                    </div>
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'dynamic-map' && (
          <div className='dynamic-map'>
            <div className='feature-intro'>
            </div>
            <div className='forecast-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Standard Map Layers</h3>
                <div className='feature-image' />
                <div className='section-content map-layers-list'>
                  <div className='map-layer-item'>
                    <h4><i className='layer-icon radar'></i>Radar</h4>
                    <p>Visualizes precipitation patterns to help you know when rain or snow is approaching. Press the play button to view the radar for over 1 hour.</p>
                  </div>
                  <div className='map-layer-item'>
                    <h4><i className='layer-icon temp'></i>Temperature</h4>
                    <p>Keeps you updated on current temperatures so you can plan your day accordingly. Includes Max and Min Temperature for the selected location.</p>
                  </div>
                  <div className='map-layer-item'>
                    <h4><i className='layer-icon wind'></i>Wind and Lightning</h4>
                    <p>rovides real-time wind speed and direction, vital for travel and outdoor activities. Includes Max Gust and Max Wind for the selected location. Select the lightning icon to view real-time lightning strikes.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>Advanced Map Layers</h3>
                <div className='feature-image' />
                <div className='section-content map-layers-list'>
                  <div className='map-layers-grid'>
                    <div className='map-layer-item'>
                      <h4><i className='layer-icon fires'></i>Fires</h4>
                      <p>Displays active wildfires across the U.S. and Canada with fire start date, cause, area, and containment percentage. Critical for safety and preparedness in fire-prone regions.</p>
                    </div>
                    <div className='map-layer-item'>
                      <h4><i className='layer-icon cyclones'></i>Tropical Cyclones</h4>
                      <p>Track active tropical cyclones including storms, hurricanes, and typhoons worldwide with detailed forecasts from NHC, CPHC, and JTWC. Essential for coastal preparedness.</p>
                    </div>
                    <div className='map-layer-item'>
                      <h4><i className='layer-icon snow'></i>Snow Depth</h4>
                      <p>View snow accumulation data in inches, updated every 6 hours. Click colored regions for detailed snow conditions including rate, visibility, temperature, and wind speed.</p>
                    </div>
                    <div className='map-layer-item'>
                      <h4><i className='layer-icon air'></i>Air Quality</h4>
                      <p>Track Air Quality Index (AQI) data globally, updated every 12 hours and categorized from 'Good' to 'Hazardous.' Perfect for planning outdoor activities, especially for those with respiratory concerns.</p>
                    </div>
                    <div className='map-layer-item'>
                      <h4><i className='layer-icon river'></i>River Gauge</h4>
                      <p>Updated hourly with river levels and flow rates from NOAA. Provides real-time data on water height, flow, and flood impacts—essential for water activities and flood-prone areas.</p>
                    </div>
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'dash' && (
          <div className='dashboard'>
            <div className='feature-intro'>
              <p>Your personalized weather dashboard brings all your station data together in one clear view. Easily track live readings, customize your layout, and visualize key conditions in real time.</p>
            </div>
            <div className='equal-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Standard Dashboard</h3>
                <div className='feature-image' />
                <div className='section-content'>
                  <div>
                    <h4>Core Metrics</h4>
                    <p>Access real-time weather data from your connected station or a favored station, all in one convenient view.</p>
                  </div>
                  <div>
                    <h4>Live Data Updates</h4>
                    <p>Watch your dashboard refresh automatically with the latest readings, so your weather view is always up to date.</p>
                  </div>
                  <div>
                    <h4>Custom Dashboard Layouts</h4>
                    <p>Rearrange and organize your panels to display the information that matters most to you.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>AWN+ Dashboard</h3>
                <div className='feature-image' />
                <div className='section-content'>
                  <div>
                    <h4>Growing, Heating, and Cooling Degree Days</h4>
                    <p>Track degree days tailored to agriculture, HVAC efficiency, or seasonal comfort trends, all calculated automatically and displayed on your dashboard.</p>
                  </div>
                  <div>
                    <h4>Premium Map Layers Integration</h4>
                    <p>Unlock deeper weather insights with Tropical Cyclones, Fires, Air Quality, Snow Depth, and River Gauge map layers, available right from your dashboard for a complete picture of your environment.</p>
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'data-graphing' && (
          <div className='data-graphing'>
            <div className='feature-intro'>
              <p>Visualize your weather data with powerful charting tools to identify trends and patterns.</p>
            </div>
            <div className='equal-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Standard Graphing</h3>
                <div className='feature-image' />
                <div className='section-content'>
                  <div>
                    <h4>Standard Graphing</h4>
                    <p>View interactive charts of your station data to see how conditions shift throughout the day. Choose variables like temperature, humidity, and rainfall to explore short-term trends.</p>
                  </div>
                  <div>
                    <h4>24-Hour Data History</h4>
                    <p>Access up to 24 hours of stored data for quick reviews and daily monitoring.</p>
                  </div>
                  <div>
                    <h4>Graph Customization Tools</h4>
                    <p>Adjust your chart view by selecting parameters, changing date ranges, or switching between data types for a clearer perspective.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>AWN+ Advanced Graphing</h3>
                <div className='feature-image' />
                <div className='section-content'>
                  <div>
                    <h4>Multi-Station & Multi-Parameter Graphing</h4>
                    <p>View multiple stations or parameters on a single graph, providing comprehensive insights in one view.</p>
                  </div>
                  <div>
                    <h4>Multi-Axis Display</h4>
                    <p>Each parameter appears on its own axis for a clean, easy-to-read graph with no overlapping data.</p>
                  </div>
                  <div>
                    <h4>Flexible Graph Views</h4>
                    <p>Choose bar or line styles per parameter to highlight relationships like temperature vs. rainfall.</p>
                  </div>
                  <div>
                    <h4>Organize & Edit Graphs Easily</h4>
                    <p>Rename, reorder, or duplicate graphs to quickly build and refine your comparisons.</p>
                  </div>
                  <div>
                    <h4>Quick Zoom & Interactive Toggles</h4>
                    <p>Zoom into specific time ranges and toggle parameters or stations on and off to focus on what matters most.</p>
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'alerts' && (
          <div className='alerts'>
            <div className='feature-intro'>
              <p>Stay informed with customizable weather alerts delivered to your preferred channels.</p>
            </div>
            <div className='forecast-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Standard Alerts</h3>
                <div className='section-content'>
                  <div>
                    <h4>Email Notifications</h4>
                    <p>Receive essential weather alerts directly in your inbox when conditions meet your chosen thresholds.</p>
                  </div>
                  <div>
                    <h4>In-App Push Alerts</h4>
                    <p>Get real-time notifications within the Ambient Weather Network app for instant awareness.</p>
                  </div>
                  <div>
                    <h4>Timed Alerts (Maintenance Alerts)</h4>
                    <p>Get notified for low battery levels, sensor cleaning, and your weather station upkeep needs.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>Advanced Alerts</h3>
                <div className='section-content'>
                  <div className='forecast-grid'>
                    <div>
                      <h3>AWN+</h3>
                      <h4>SMS Alerts<br/>(10 per day)</h4>
                      <p>Get up to 10 text message alerts per day for your selected weather conditions. Perfect for quick updates when you're away from your dashboard or on the move.</p>
                    </div>
                    <div>
                      <h3>AWN+ for Teams</h3>
                      <h4>SMS Alerts<br/>(20 per day)</h4>
                      <p>Expand your reach with up to 20 SMS alerts per day, shared across your team. Notify up to five members simultaneously when set thresholds are met—ideal for group coordination in schools, worksites, or safety operations.</p>
                    </div>
                    <div className='feature-image' />
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'devices' && (
          <div className='devices'>
            <div className='feature-intro'>
              <p>Manage and monitor your connected weather stations and sensors.</p>
            </div>
            <div className='equal-sections'>
              <div className='map-layers-section basic'>
                <h3 className='section-header'>Basic Device Management</h3>
                <div className='section-content'>
                  <div>
                    <h4>Single Station</h4>
                    <p>Monitor and configure a single weather station with basic sensor readings, connectivity status, and simple device settings for straightforward weather tracking.</p>
                  </div>
                  <SectionActions sectionType="basic" />
                </div>
              </div>
              <div className='map-layers-section advanced'>
                <h3 className='section-header'>Advanced Device Management</h3>
                <div className='section-content'>
                  <div>
                    <h4>Multi-Device Network</h4>
                    <p>Manage multiple weather stations, configure sensor arrays, access advanced diagnostics, and integrate third-party devices for enterprise-level weather monitoring networks.</p>
                  </div>
                  <SectionActions sectionType="advanced" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

FeatureTabs.propTypes = {}
FeatureTabs.defaultProps = {}

export default withRouter(FeatureTabs)

FeatureTabs.displayName = 'FeatureTabs'
